/**
 * components/client-detail/DossierDocSidebar.tsx
 *
 * Left sidebar panel (~320px) for the dossier builder step 1.
 * Mirrors the CatalogSidebar pattern used in the form creation wizard:
 * - Desktop: fixed 320px panel with border-r
 * - Mobile: floating FAB + bottom-sheet overlay
 *
 * Documents are grouped by piece label with section headers.
 * Each doc row shows its inclusion state:
 * - Included (completed + pdfPath + not excluded): green Check on left, swaps to red X on hover
 *   with red background tint; click excludes the doc.
 * - Available (completed + pdfPath + excluded): empty space on left, swaps to blue Plus on hover
 *   with blue background tint; click includes the doc.
 * - Failed: error icon + retry button
 * - Converting: spinner, non-interactive
 *
 * Action icons (+ / X) use a "swap in place" pattern: they replace the status icon in the
 * same left-side slot on hover, keeping the layout stable (3 columns: icon | name | preview).
 * Eye icon is always shown for quick preview (triggers onPreviewDoc).
 * Green flash animation (~600ms) when a doc is included.
 *
 * Created Apr 2026.
 * Updated Apr 2026: added onExcludeDoc + X button on included rows.
 * Updated Apr 2026: swap-in-place interaction -- action icons (+/X) appear at the same
 *   position as the status icon (left slot) on hover, replacing right-side badges.
 */

import { useState } from 'react';
import { Search, Plus, X, Check, Eye, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import type { DossierPiece, DocumentEntry } from '../../types/dossier';

interface DossierDocSidebarProps {
  pieces: DossierPiece[];
  excludedDocIds: Set<string>;
  retryingDocIds: Set<string>;
  onIncludeDoc: (docId: string) => void;
  onExcludeDoc: (docId: string) => void;
  onPreviewDoc: (doc: DocumentEntry) => void;
  onRetryDoc: (docId: string, requestId: string) => void;
}

interface DocRowProps {
  doc: DocumentEntry;
  isIncluded: boolean;
  isExcluded: boolean;
  isFailed: boolean;
  isConverting: boolean;
  isRetrying: boolean;
  onInclude: () => void;
  onExclude: () => void;
  onPreview: () => void;
  onRetry: () => void;
}

function DocRow({
  doc,
  isIncluded,
  isExcluded,
  isFailed,
  isConverting,
  isRetrying,
  onInclude,
  onExclude,
  onPreview,
  onRetry,
}: DocRowProps) {
  const [showFlash, setShowFlash] = useState(false);
  const name = doc.displayFilename ?? doc.originalFilename;

  const handleInclude = () => {
    if (!isExcluded) return;
    setShowFlash(true);
    onInclude();
    setTimeout(() => setShowFlash(false), 600);
  };

  const previewButton = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPreview(); }}
      className="flex items-center justify-center w-6 h-6 rounded text-neutral-300 hover:text-primary-500 hover:bg-primary-50 transition-all flex-shrink-0 opacity-0 group-hover:opacity-100"
      title="Aperçu"
      aria-label="Aperçu du fichier"
    >
      <Eye className="w-3.5 h-3.5" />
    </button>
  );

  if (isIncluded) {
    return (
      <button
        type="button"
        onClick={onExclude}
        className={`group flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left transition-all duration-300 hover:bg-error-50/40 cursor-pointer ${
          showFlash ? 'bg-success-50 ring-2 ring-success-200' : 'bg-neutral-50'
        }`}
        title="Retirer du dossier"
        aria-label="Retirer du dossier"
      >
        <div className="relative w-3.5 h-3.5 flex-shrink-0">
          <Check className="absolute inset-0 w-3.5 h-3.5 text-success-500 transition-opacity duration-150 group-hover:opacity-0" />
          <X className="absolute inset-0 w-3.5 h-3.5 text-error-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        </div>
        <span className="flex-1 text-xs font-medium text-neutral-700 truncate" title={name}>
          {name}
        </span>
        {previewButton}
      </button>
    );
  }

  if (isFailed) {
    return (
      <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-error-50">
        <AlertCircle className="w-3.5 h-3.5 text-error-400 flex-shrink-0" />
        <span className="flex-1 text-xs font-medium text-error-600 truncate" title={name}>
          {name}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="flex items-center justify-center w-6 h-6 rounded text-error-300 hover:text-error-500 hover:bg-error-100 transition-all flex-shrink-0 opacity-0 group-hover:opacity-100"
          title="Aperçu"
          aria-label="Aperçu du fichier"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="flex items-center gap-1 shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-white border border-error-300 text-error-600 hover:bg-error-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title="Réessayer la conversion"
        >
          {isRetrying ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
        </button>
      </div>
    );
  }

  if (isConverting) {
    return (
      <div className="group flex items-center gap-2 px-3 py-2 rounded-lg">
        <Loader2 className="w-3.5 h-3.5 text-neutral-400 animate-spin flex-shrink-0" />
        <span className="flex-1 text-xs font-medium text-neutral-400 truncate" title={name}>
          {name}
        </span>
      </div>
    );
  }

  if (isExcluded) {
    return (
      <button
        type="button"
        onClick={handleInclude}
        className={`group flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left transition-all duration-300 hover:bg-primary-50/40 cursor-pointer ${
          showFlash ? 'bg-success-50 ring-2 ring-success-200' : ''
        }`}
      >
        <div className="relative w-3.5 h-3.5 flex-shrink-0">
          <Plus className="absolute inset-0 w-3.5 h-3.5 text-primary-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        </div>
        <span className="flex-1 text-xs font-medium text-neutral-500 truncate" title={name}>
          {name}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="flex items-center justify-center w-6 h-6 rounded text-neutral-300 hover:text-primary-500 hover:bg-primary-50 transition-all flex-shrink-0 opacity-0 group-hover:opacity-100"
          title="Aperçu"
          aria-label="Aperçu du fichier"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      </button>
    );
  }

  return null;
}

export default function DossierDocSidebar({
  pieces,
  excludedDocIds,
  retryingDocIds,
  onIncludeDoc,
  onExcludeDoc,
  onPreviewDoc,
  onRetryDoc,
}: DossierDocSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const getDocStatus = (doc: DocumentEntry) => {
    const isSelectable = doc.uploadStatus === 'completed' && doc.pdfPath != null;
    const isFailed = doc.uploadStatus === 'failed';
    const isConverting = doc.uploadStatus === 'converting' || doc.uploadStatus === 'uploading';
    const isExcluded = isSelectable && excludedDocIds.has(doc.id);
    const isIncluded = isSelectable && !excludedDocIds.has(doc.id);
    return { isIncluded, isExcluded, isFailed, isConverting, isSelectable };
  };

  const matchesSearch = (doc: DocumentEntry) => {
    if (!searchQuery) return true;
    const name = (doc.displayFilename ?? doc.originalFilename).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  };

  const filteredPieces = pieces
    .map((piece) => ({
      ...piece,
      docs: piece.docs.filter(matchesSearch),
    }))
    .filter((piece) => piece.docs.length > 0);

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-shrink-0 p-4 border-b border-neutral-200">
        <div className="flex items-center justify-between mb-4 lg:hidden">
          <h2 className="text-base font-semibold text-neutral-900">Documents disponibles</h2>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-600" />
          </button>
        </div>

        <p className="hidden lg:block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-3">
          Documents disponibles
        </p>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Rechercher un fichier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredPieces.length === 0 ? (
          <div className="flex items-center justify-center h-32 p-6">
            <p className="text-sm text-neutral-400 text-center">
              {searchQuery ? 'Aucun résultat trouvé' : 'Aucun document disponible'}
            </p>
          </div>
        ) : (
          <div className="py-2">
            {filteredPieces.map((piece) => (
              <div key={piece.id}>
                <div className="px-3 pt-3 pb-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 truncate" title={piece.label}>
                    {piece.label}
                  </p>
                </div>
                <div className="px-3 space-y-0.5">
                  {piece.docs.map((doc) => {
                    const { isIncluded, isExcluded, isFailed, isConverting } = getDocStatus(doc);
                    const isRetrying = retryingDocIds.has(doc.id);
                    return (
                      <DocRow
                        key={doc.id}
                        doc={doc}
                        isIncluded={isIncluded}
                        isExcluded={isExcluded}
                        isFailed={isFailed}
                        isConverting={isConverting}
                        isRetrying={isRetrying}
                        onInclude={() => onIncludeDoc(doc.id)}
                        onExclude={() => onExcludeDoc(doc.id)}
                        onPreview={() => onPreviewDoc(doc)}
                        onRetry={() => onRetryDoc(doc.id, doc.requestId)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block w-80 flex-shrink-0 border-r border-neutral-200 h-full">
        {sidebarContent}
      </div>

      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed bottom-20 right-6 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-soft-lg hover:shadow-soft-xl transition-all flex items-center justify-center z-40"
        aria-label="Voir les documents disponibles"
      >
        <Plus className="w-6 h-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-neutral-900/50 z-40 animate-fade-in"
            onClick={() => setIsOpen(false)}
          />
          <div className="lg:hidden fixed inset-x-0 bottom-0 top-20 bg-white rounded-t-2xl shadow-soft-xl z-50 animate-slide-up">
            {sidebarContent}
          </div>
        </>
      )}
    </>
  );
}
